import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import XIcon from '@mui/icons-material/X';
import { buildReferralLink, socialShareLinks } from '../lib/referralCapture';

function rewardCopy(rewards = {}) {
  const signup = Number(rewards.referredSignupBonus || 50);
  const referrer = Number(rewards.referrerActivationBonus || 100);
  return `Get free ${referrer} credits now`;
}

export function ViralShareCard({ referral = {}, region = '', compact = false, onToast }) {
  const code = referral?.code || '';
  const inviteUrl = buildReferralLink(code);
  const shareText = 'NudgeForMe finds missed follow-ups hiding in your inbox and drafts the replies. Try it with my invite link.';
  const links = socialShareLinks({ url: inviteUrl, text: shareText });

  const copy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard?.writeText(inviteUrl);
      onToast?.('success', 'Invite link copied. Share it to earn more Nudge credits.');
    } catch {
      onToast?.('warning', 'Could not copy automatically. Select the invite link manually.');
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: 'rgba(255,179,71,.28)',
        bgcolor: 'rgba(255,253,248,.92)',
        boxShadow: compact ? 'none' : undefined,
        '& .MuiButton-root': { borderRadius: 1 },
      }}
    >
      <CardContent sx={{ p: compact ? 2 : 2.5 }}>
        <Stack direction={{ xs: 'column', md: compact ? 'column' : 'row' }} spacing={2} alignItems={{ md: compact ? 'flex-start' : 'center' }} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box sx={{ width: 34, height: 34, borderRadius: 1.2, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,179,71,.16)', color: 'primary.dark' }}>
                <RedeemRoundedIcon fontSize="small" />
              </Box>
              <Typography fontWeight={900}>{rewardCopy(referral?.rewards)}</Typography>
              <Chip size="small" label="Early access" sx={{ borderRadius: 1, bgcolor: 'rgba(141,149,255,.10)' }} />
            </Stack>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              Your friend sees the public site first, then gets {Number(referral?.rewards?.referredSignupBonus || 50)} credits immediately after signing up with your link. You get {Number(referral?.rewards?.referrerActivationBonus || 100)} credits when they connect a mailbox and activate Nudge.
            </Typography>
            <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
              <Chip size="small" label={`Friend gets ${Number(referral?.rewards?.referredSignupBonus || 50)} now`} sx={{ borderRadius: 1, bgcolor: 'rgba(74,194,143,.12)' }} />
              <Chip size="small" label={`You get ${Number(referral?.rewards?.referrerActivationBonus || 100)} after activation`} sx={{ borderRadius: 1, bgcolor: 'rgba(255,179,71,.16)' }} />
            </Stack>
            {code && (
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-flex',
                  mt: 1.2,
                  px: 1,
                  py: .45,
                  borderRadius: 1,
                  bgcolor: 'rgba(26,29,49,.05)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: 'text.secondary',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={inviteUrl}
              >
                {inviteUrl || `Referral code: ${code}`}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: '0 0 auto' }}>
            <Button variant="contained" startIcon={<ContentCopyRoundedIcon />} onClick={copy} disabled={!inviteUrl}>Copy link</Button>
            <Button component="a" href={links.linkedin} target="_blank" rel="noreferrer" variant="outlined" startIcon={<LinkedInIcon />} disabled={!inviteUrl}>LinkedIn</Button>
            <Button component="a" href={links.x} target="_blank" rel="noreferrer" variant="outlined" startIcon={<XIcon />} disabled={!inviteUrl}>X</Button>
            <Button component="a" href={links.email} variant="outlined" startIcon={<EmailRoundedIcon />} disabled={!inviteUrl}>Email</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
